import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
import { api, ApiClientError } from "@/lib/api";

// Frontend Agency type with computed fields
interface AgencyDisplay {
  id: string;
  agency_name: string;
  abbreviation: string;
  contact: string;
  users: number;
  active_cases: number;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  date_added: string; // ISO timestamp
}

const PAGE_SIZE = 50;

export function AgencyList() {
  const { logout } = useAuth();
  const [agencies, setAgencies] = useState<AgencyDisplay[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadAgencies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.agencies.list(PAGE_SIZE);
      // Transform backend Agency to frontend AgencyDisplay format
      // TODO: Fetch stats (users, cases, URLs) for each agency
      const transformedAgencies: AgencyDisplay[] = data.map((agency) => ({
        id: agency.id,
        agency_name: agency.name,
        abbreviation: agency.abbreviation || "N/A",
        contact: agency.contact_email || "N/A",
        users: 0, // TODO: Count users with this agency_id
        active_cases: 0, // TODO: Count cases for this agency
        active_urls: 0, // TODO: Count URLs in active cases for this agency
        removed_urls: 0, // TODO: Count removed URLs in cases for this agency
        takedown_rate: 0, // TODO: Calculate from URL stats
        date_added: agency.created_at,
      }));
      setAgencies(transformedAgencies);
      setHasMore(data.length === PAGE_SIZE);
      setTotalCount(data.length);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to load agencies");
        }
      } else {
        setError("Failed to load agencies");
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Agencies table
  const columns: ColumnDef<AgencyDisplay>[] = [
    {
      key: "agency_name",
      header: "Agency Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.agency_name}
        </div>
      ),
      sortValue: (item) => item.agency_name,
    },
    {
      key: "abbreviation",
      header: "Abbreviation",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.abbreviation}
        </div>
      ),
      sortValue: (item) => item.abbreviation,
    },
    {
      key: "contact",
      header: "Contact",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.contact}</div>
      ),
      sortValue: (item) => item.contact,
    },
    {
      key: "users",
      header: "Users",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.users}</div>
      ),
      sortValue: (item) => item.users,
    },
    {
      key: "active_cases",
      header: "Active Cases",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_cases}</div>
      ),
      sortValue: (item) => item.active_cases,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "date_added",
      header: "Date Added",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {new Date(item.date_added).toLocaleDateString()}
        </div>
      ),
      sortValue: (item) => new Date(item.date_added).getTime(),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to agency details or open modal
            console.log("View agency:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Active Cases", placeholder: "All" },
    { label: "User Count", placeholder: "All" },
  ];

  // Handle new agency creation
  const handleNewAgency = () => {
    // TODO: Implement new agency modal or navigation
    console.log("Create new agency");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export agencies");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewAgency}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        New Agency
      </Button>
    </>
  );

  return (
    <DataTable
      title="Agency Management"
      subtitle="Manage law enforcement agencies and their accounts"
      columns={columns}
      data={agencies}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search agencies..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="agencies"
    />
  );
}

